'use client';
import { useEffect, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrivy } from '@privy-io/react-auth';
import { PrivyLoginButton } from '@/components/privy-login-button';
import { useIsLoggedIn, useUserInfo, useUserStore } from '@/app/stores/useUserStore';
import { usePredictStore } from '@/app/stores/usePredictStore';
import CoSearch from '@/components/header/co-search';
import CoSetting from '@/components/header/co-setting';
import CoUser from '@/components/header/co-user';
import CoDeposit from '@/components/header/co-deposit';
import CoHistory from '@/components/header/co-history';
import CoMessage from '@/components/header/co-message';
import InviteLive from '@/app/components/Me/Invite/live';
import { Button } from '@/components/ui/button';
import { useSessionSignerAuth } from '@/hooks/wallet/use-session-signer-auth';
import { goToLogin } from '@/lib/login';
import { useTranslation } from 'react-i18next';
import CoReserve from '@/components/header/co-reserve';
import CoStreamTools from '@/components/header/co-stream-tools';
import { WHITE_LIST_PATHS, WHITE_LIST_PATHS_WITH_REFRESH } from '@/contexts/global-context';

function MainHeaderComponent({
  hideHeader = false,
  isFullScreen = true,
  hideLive = false,
  hideSearch = false,
  className,
}: {
  hideHeader?: boolean;
  isFullScreen?: boolean;
  hideLive?: boolean;
  hideSearch?: boolean;
  className?: string;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLoggedIn = useIsLoggedIn();
  const userInfo = useUserInfo();
  const { clearUserInfo } = useUserStore();
  const { ready: privyReady, authenticated: privyAuthenticated } = usePrivy();
  const { checkAndAuthorize } = useSessionSignerAuth();
  const fetchFeeConfig = usePredictStore(state => state.fetchFeeConfig);

  // 使用 ref 确保只执行一次
  const hasExecuted = useRef(false);

  // 检测 Privy 会话和后端登录状态不同步
  // 场景：后端 token 有效，但 Privy 会话已断开（token 过期/被清除）
  // 每次依赖变化都会检测（纯本地状态，无网络请求）
  useEffect(() => {
    if (privyReady && !privyAuthenticated && isLoggedIn) {
      console.log('⚠️ privy not authenticated, clear user info, redirect to discover page');
      clearUserInfo();
      // redirect to discover page to let user re-login
      const redirectRefreshPath = WHITE_LIST_PATHS_WITH_REFRESH.find(path => location.pathname.startsWith(path));
      if (redirectRefreshPath) {
        window.location.reload();
      } else {
        const redirectPath = WHITE_LIST_PATHS.find(path => location.pathname.startsWith(path));
        if (redirectPath) {
          navigate(redirectPath);
        } else {
          goToLogin()
        }
      }
    }
  }, [privyReady, privyAuthenticated, isLoggedIn, clearUserInfo, navigate]);

  // 检查并自动授权 Session Signer
  useEffect(() => {
    if (!hasExecuted.current) {
      hasExecuted.current = true;
      checkAndAuthorize();
    }
  }, [checkAndAuthorize]);

  // 登录后拉取手续费配置（只需拉取一次）
  useEffect(() => {
    if (isLoggedIn) {
      fetchFeeConfig();
    }
  }, [isLoggedIn, fetchFeeConfig]);

  if (hideHeader) {
    return null;
  }
  return (
    <header className={`co-header backdrop-blur-md sticky top-0 z-50 shadow-sm dark:shadow-none ${className || ''}`}>
      <div className={isFullScreen ? 'mx-4' : 'max-w-7xl mx-auto'}>
        <div className="flex items-center justify-between h-[60px]">
          <div
            onClick={() => {
              navigate('/');
            }}
            className="flex items-center co-logo cursor-pointer max-md:hidden"
          />
          <div
            onClick={() => {
              navigate('/');
            }}
            className="md:hidden flex items-center co-logo-small cursor-pointer"
          />
          {/* 搜索和用户操作区域 */}
          <div className="flex items-center space-x-1 md:space-x-3 justify-end flex-1">
            <Button
              className="flex md:hidden h-8"
              size="smm"
              onClick={() => {
                navigate('/app-download/');
              }}
            >
              {t('header.downloadApp')}
            </Button>
            {/* 搜索框 */}
            {!hideSearch ? <CoSearch /> : null}
            {/* 操作按钮组 */}
            <div className="flex items-center gap-1 md:gap-3">
              {/* 用户头像下拉菜单 */}
              {!isLoggedIn ? (
                <div className="flex items-center gap-2">
                  <PrivyLoginButton className='rounded-lg'/>
                  <CoStreamTools />
                  <CoSetting />
                </div>
              ) : (
                <>
                  {!hideLive && <InviteLive isLive={!!userInfo?.isRoom || false} isStreamer={!!userInfo?.isStreamer || false} />}
                  {/* 充值按钮 */}
                  <CoDeposit />
                  <CoHistory />
                  <CoReserve />
                  <CoMessage />
                  <CoStreamTools />
                  <div className="hidden md:block w-px h-6 bg-button-border/75 mx-1" />
                  <CoUser isLive={!!userInfo?.isRoom || false} />
                  <CoSetting />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

// 使用 memo 优化，防止不必要的重新渲染
export const MainHeader = memo(MainHeaderComponent);
